import React, { useState, useCallback, useRef } from 'react'
import { useIsMobile } from '../hooks/useIsMobile'
import { useQueryClient } from '@tanstack/react-query'
import { useTheme } from '../hooks/useTheme'
import { usePreferencesSync } from '../hooks/usePreferencesSync'
import { THEMES } from '../themes'
import { useUIStore } from '../store/ui'
import type { ThemeMode } from '../store/ui'
import { useAPIKeys, useCreateAPIKey, useDeleteAPIKey } from '../api/apiKeys'
import type { APIKey, CreatedAPIKey } from '../api/apiKeys'
import { apiFetch } from '../api/client'
import { useToast } from '../components/primitives/Toast'
import { NotificationsSection } from '../components/settings/NotificationsSection'
import pkg from '../../package.json'

const MODE_OPTIONS: { value: ThemeMode; label: string; desc: string }[] = [
  { value: 'light', label: 'Light', desc: 'Cream paper' },
  { value: 'dark', label: 'Dark', desc: 'Night ink' },
  { value: 'system', label: 'System', desc: 'Follow OS setting' },
]

export function SettingsPage() {
  const { themeMode, setThemeMode } = useTheme()
  const mutation = usePreferencesSync()
  const isMobile = useIsMobile()

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: isMobile ? '24px 16px 48px' : '64px 32px 48px' }}>
        <h1 className='display-serif' style={{ marginBottom: '40px' }}>
          Settings
        </h1>

        {/* Appearance section — mode selector + palette picker */}
        <SettingsSection title='Appearance'>
          {/* Mode selector (Light / Dark / System) */}
          <div style={{ marginBottom: '20px' }}>
            <p className="eyebrow" style={{ marginBottom: '10px', color: 'var(--color-ink-muted)' }}>
              Mode
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {MODE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setThemeMode(t.value)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: themeMode === t.value ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                    background: themeMode === t.value ? 'var(--color-paper-dark)' : 'var(--color-surface)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-serif)',
                    fontSize: '0.875rem',
                    color: themeMode === t.value ? 'var(--color-ink)' : 'var(--color-ink-muted)',
                    textAlign: 'left',
                    width: '140px',
                  }}
                >
                  <div style={{ fontWeight: themeMode === t.value ? 600 : 400 }}>{t.label}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-ink-faint)' }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Palette picker */}
          <div>
            <p className="eyebrow" style={{ marginBottom: '10px', color: 'var(--color-ink-muted)' }}>
              Theme
            </p>
            <ThemePalettePicker mutation={mutation} />
          </div>
        </SettingsSection>

        {/* Behavior section */}
        <BehaviorSection />

        {/* Notifications */}
        <SettingsSection title='Notifications'>
          <NotificationsSection />
        </SettingsSection>

        {/* API Keys */}
        <SettingsSection title='API Keys'>
          <APIKeysSection />
        </SettingsSection>

        {/* Keyboard shortcuts */}
        <SettingsSection title='Keyboard Shortcuts'>
          <div style={{ display: 'grid', gap: '6px' }}>
            {[
              ['⌘K', 'Open command palette'],
              ['⌘/', "Focus today's input"],
              ['T', 'Jump to today'],
              ['← / →', 'Previous / next week'],
              ['⌘D', 'Toggle dark mode'],
            ].map(([key, label]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span style={{ fontFamily: 'var(--font-serif)', fontSize: '0.875rem', color: 'var(--color-ink)' }}>{label}</span>
                <kbd
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.75rem',
                    padding: '2px 6px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '3px',
                    color: 'var(--color-ink-muted)',
                    background: 'transparent',
                  }}
                >
                  {key}
                </kbd>
              </div>
            ))}
          </div>
        </SettingsSection>

        {/* About */}
        <SettingsSection title='About todux'>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: '0.875rem', color: 'var(--color-ink-muted)' }}>
            <p style={{ fontStyle: 'italic' }}>todux — a distraction-free weekly planner.</p>
            <p style={{ marginTop: '6px' }}>Version {pkg.version}</p>
            <p style={{ marginTop: '6px', fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--color-ink-faint)' }}>
              Paper aesthetic, deliberate thought.
            </p>
          </div>
        </SettingsSection>
      </div>
    </div>
  )
}

// ── Behavior section ──────────────────────────────────────────────────────────

function BehaviorSection() {
  const smartStatusSort = useUIStore((s) => s.smartStatusSort)
  const setSmartStatusSort = useUIStore((s) => s.setSmartStatusSort)
  const [reordering, setReordering] = useState(false)
  const qc = useQueryClient()
  const { showToast } = useToast()

  const handleSmartSortChange = useCallback(
    async (val: boolean) => {
      if (val === smartStatusSort) return

      if (!val) {
        setSmartStatusSort(false)
        return
      }

      // Toggling ON: ask the backend to reorder existing todos by status first.
      setReordering(true)
      try {
        await apiFetch<{ reordered: number }>('/todos/reorder-by-status', { method: 'POST' })
        setSmartStatusSort(true)
        await qc.invalidateQueries({ queryKey: ['todos'] })
      } catch {
        showToast('Failed to reorder todos. Please try again.', 'error')
      } finally {
        setReordering(false)
      }
    },
    [smartStatusSort, setSmartStatusSort, qc, showToast],
  )

  return (
    <SettingsSection title='Behavior'>
      <div>
        <p className="eyebrow" style={{ marginBottom: '10px', color: 'var(--color-ink-muted)' }}>
          Smart status sort
        </p>
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '0.8125rem',
            color: 'var(--color-ink-faint)',
            marginBottom: '10px',
            lineHeight: 1.5,
          }}
        >
          Show completed todos first, then the rest. Auto-reorders as status changes.
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          {([true, false] as const).map((val) => {
            const active = smartStatusSort === val
            const isDisabled = reordering && val
            return (
              <button
                key={String(val)}
                onClick={() => { void handleSmartSortChange(val) }}
                disabled={isDisabled}
                style={{
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: active ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                  background: active ? 'var(--color-paper-dark)' : 'var(--color-surface)',
                  cursor: isDisabled ? 'default' : 'pointer',
                  fontFamily: 'var(--font-serif)',
                  fontSize: '0.875rem',
                  color: active ? 'var(--color-ink)' : 'var(--color-ink-muted)',
                  fontWeight: active ? 600 : 400,
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                {reordering && val ? 'Reordering…' : val ? 'On' : 'Off'}
              </button>
            )
          })}
        </div>
      </div>
    </SettingsSection>
  )
}

// ── API Keys section ──────────────────────────────────────────────────────────

function APIKeysSection() {
  const { data: keys = [], isLoading } = useAPIKeys()
  const deleteKey = useDeleteAPIKey()
  const [showCreateModal, setShowCreateModal] = useState(false)

  return (
    <div>
      <p
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '0.875rem',
          color: 'var(--color-ink-muted)',
          marginBottom: '16px',
          lineHeight: 1.55,
        }}
      >
        Generate API keys to let AI assistants and third-party tools access your todos via the
        public API at <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>/api/v1</code>.
        Maximum 10 active keys.
      </p>

      {isLoading ? (
        <p style={{ fontFamily: 'var(--font-serif)', fontSize: '0.875rem', color: 'var(--color-ink-faint)' }}>
          Loading…
        </p>
      ) : keys.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '0.875rem',
            color: 'var(--color-ink-faint)',
            fontStyle: 'italic',
            marginBottom: '16px',
          }}
        >
          No API keys yet.
        </p>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          {keys.map((k) => (
            <APIKeyRow key={k.id} apiKey={k} onRevoke={(id) => deleteKey.mutate(id)} />
          ))}
        </div>
      )}

      <button
        onClick={() => setShowCreateModal(true)}
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '0.875rem',
          padding: '8px 14px',
          background: 'var(--color-paper-dark)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-ink)',
          cursor: 'pointer',
          transition: 'border-color 0.15s',
        }}
      >
        + Create new key
      </button>

      {showCreateModal && (
        <CreateKeyModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  )
}

interface APIKeyRowProps {
  apiKey: APIKey
  onRevoke: (id: string) => void
}

function APIKeyRow({ apiKey, onRevoke }: APIKeyRowProps) {
  const [confirming, setConfirming] = useState(false)
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMobile = useIsMobile()

  const handleRevokeClick = useCallback(() => {
    if (confirming) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
      onRevoke(apiKey.id)
      setConfirming(false)
    } else {
      setConfirming(true)
      // Auto-reset "Sure?" state after 3 seconds if user doesn't confirm
      confirmTimerRef.current = setTimeout(() => setConfirming(false), 3000)
    }
  }, [confirming, onRevoke, apiKey.id])

  const createdDate = new Date(apiKey.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const lastUsedDate = apiKey.lastUsedAt
    ? new Date(apiKey.lastUsedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Never'

  if (isMobile) {
    return (
      <div
        style={{
          padding: '10px 0',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontFamily: 'var(--font-serif)', fontWeight: 600, fontSize: '0.875rem', color: 'var(--color-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {apiKey.name}
          </span>
          <button
            onClick={handleRevokeClick}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.75rem',
              padding: '3px 8px',
              background: confirming ? 'color-mix(in srgb, var(--color-prio-high) 12%, transparent)' : 'transparent',
              border: `1px solid ${confirming ? 'var(--color-prio-high)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-sm)',
              color: confirming ? 'var(--color-prio-high)' : 'var(--color-ink-muted)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {confirming ? 'Sure?' : 'Revoke'}
          </button>
        </div>
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--color-ink-muted)' }}>
          {apiKey.prefix}…{apiKey.last4}
        </code>
        <div style={{ marginTop: '2px', fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-ink-faint)' }}>
          Created {createdDate} · Last used {lastUsedDate}
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      {/* Name */}
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontWeight: 600,
          fontSize: '0.875rem',
          color: 'var(--color-ink)',
          flex: '0 0 140px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {apiKey.name}
      </span>

      {/* Key fragment */}
      <code
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.8125rem',
          color: 'var(--color-ink-muted)',
          flex: '0 0 160px',
        }}
      >
        {apiKey.prefix}…{apiKey.last4}
      </code>

      {/* Created */}
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.75rem',
          color: 'var(--color-ink-faint)',
          flex: '0 0 100px',
        }}
      >
        {createdDate}
      </span>

      {/* Last used */}
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.75rem',
          color: 'var(--color-ink-faint)',
          flex: 1,
        }}
      >
        {lastUsedDate}
      </span>

      {/* Revoke */}
      <button
        onClick={handleRevokeClick}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.75rem',
          padding: '3px 8px',
          background: confirming ? 'color-mix(in srgb, var(--color-prio-high) 12%, transparent)' : 'transparent',
          border: `1px solid ${confirming ? 'var(--color-prio-high)' : 'var(--color-border)'}`,
          borderRadius: 'var(--radius-sm)',
          color: confirming ? 'var(--color-prio-high)' : 'var(--color-ink-muted)',
          cursor: 'pointer',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
        }}
      >
        {confirming ? 'Sure?' : 'Revoke'}
      </button>
    </div>
  )
}

// ── Create key modal ──────────────────────────────────────────────────────────

interface CreateKeyModalProps {
  onClose: () => void
}

type ModalStep = 'form' | 'revealed'

function CreateKeyModal({ onClose }: CreateKeyModalProps) {
  const createKey = useCreateAPIKey()
  const [name, setName] = useState('')
  const [step, setStep] = useState<ModalStep>('form')
  const [createdKey, setCreatedKey] = useState<CreatedAPIKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGenerate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setError(null)
    createKey.mutate(
      { name: trimmed },
      {
        onSuccess: (data) => {
          setCreatedKey(data)
          setStep('revealed')
        },
        onError: (err: unknown) => {
          const apiErr = err as { error?: { code?: string } }
          if (apiErr?.error?.code === 'max_keys_reached') {
            setError("You've reached the 10-key limit. Revoke a key before creating another.")
          } else {
            setError('Could not create key. Please try again.')
          }
        },
      }
    )
  }, [name, createKey])

  const handleCopy = useCallback(() => {
    if (!createdKey) return
    navigator.clipboard.writeText(createdKey.key).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [createdKey])

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={step === 'form' ? onClose : undefined}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 100,
        }}
      />

      {/* Modal panel */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(480px, 90vw)',
          background: 'var(--color-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)',
          padding: '32px 28px 28px',
        }}
      >
        {step === 'form' ? (
          <form onSubmit={handleGenerate}>
            <h3
              style={{
                fontFamily: 'var(--font-serif)',
                fontWeight: 600,
                fontSize: '1.125rem',
                color: 'var(--color-ink)',
                marginBottom: '20px',
              }}
            >
              Create API key
            </h3>

            <label
              htmlFor="api-key-name"
              className="eyebrow"
              style={{ display: 'block', marginBottom: '8px' }}
            >
              Name
            </label>
            <input
              id="api-key-name"
              type="text"
              autoFocus
              required
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null) }}
              placeholder="e.g. My assistant"
              style={{
                display: 'block',
                width: '100%',
                padding: '9px 12px',
                fontFamily: 'var(--font-serif)',
                fontSize: '0.9375rem',
                color: 'var(--color-ink)',
                background: 'var(--color-paper-dark)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
                marginBottom: error ? '10px' : '20px',
                boxSizing: 'border-box',
              }}
            />

            {error && (
              <p
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '0.875rem',
                  color: 'var(--color-prio-high)',
                  marginBottom: '14px',
                  lineHeight: 1.4,
                }}
              >
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '0.875rem',
                  padding: '8px 14px',
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-ink-muted)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createKey.isPending || !name.trim()}
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '0.875rem',
                  padding: '8px 14px',
                  background: 'var(--color-ink)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-paper)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {createKey.isPending ? 'Generating…' : 'Generate key'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h3
              style={{
                fontFamily: 'var(--font-serif)',
                fontWeight: 600,
                fontSize: '1.125rem',
                color: 'var(--color-ink)',
                marginBottom: '12px',
              }}
            >
              Key created
            </h3>

            {/* Warning banner */}
            <div
              style={{
                background: 'color-mix(in srgb, var(--color-prio-medium) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-prio-medium) 40%, transparent)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                marginBottom: '16px',
              }}
            >
              <p
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '0.875rem',
                  color: 'var(--color-prio-medium)',
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                Save this key now — it won't be shown again.
              </p>
            </div>

            {/* Raw key display */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'var(--color-paper-dark)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 12px',
                marginBottom: '20px',
              }}
            >
              <code
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-ink)',
                  flex: 1,
                  wordBreak: 'break-all',
                  userSelect: 'all',
                }}
              >
                {createdKey?.key}
              </code>
              <button
                onClick={handleCopy}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.75rem',
                  padding: '4px 10px',
                  background: copied
                    ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)'
                    : 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  color: copied ? 'var(--color-accent)' : 'var(--color-ink-muted)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '0.875rem',
                  padding: '8px 18px',
                  background: 'var(--color-ink)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-paper)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

interface ThemePalettePickerProps {
  mutation: ReturnType<typeof usePreferencesSync>
}

function ThemePalettePicker({ mutation }: ThemePalettePickerProps) {
  const themeId = useUIStore((s) => s.themeId)
  const setThemeId = useUIStore((s) => s.setThemeId)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {THEMES.map((t) => {
        const active = t.id === themeId
        return (
          <button
            key={t.id}
            type="button"
            title={t.name}
            aria-label={t.name}
            aria-pressed={active}
            onClick={() => {
              // Apply instantly in local store; persist server-side without blocking UI.
              setThemeId(t.id)
              mutation.mutate(t.id)
            }}
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              padding: 0,
              cursor: 'pointer',
              border: active ? '2px solid var(--color-ink)' : '1.5px solid var(--color-border)',
              // Two-tone swatch: left half = paper color, right half = accent color (light variant).
              // Hardcoded from the theme's light palette so each swatch shows its own identity
              // regardless of which theme is currently active.
              background: `linear-gradient(90deg, ${t.light.paper} 0 50%, ${t.light.accent} 50% 100%)`,
              boxShadow: active ? '0 0 0 2px var(--color-accent)' : 'none',
              transition: 'box-shadow 0.15s, border-color 0.15s',
            }}
          />
        )
      })}
    </div>
  )
}

function SettingsSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section style={{ marginBottom: '32px' }}>
      <h2 className="eyebrow" style={{ marginBottom: '14px' }}>
        {title}
      </h2>
      <hr className="hairline" style={{ marginBottom: '16px' }} />
      {children}
    </section>
  )
}
